import { useEffect } from 'react';

import { useAppDispatch } from '@/Application/Hooks/Redux';
import type {
  AccordionIndex,
  TPaymentMethodId
} from '@/modules/perseo-billing/components/accordion/interfaces';
import { useAccordion } from '@/modules/perseo-billing/components/accordion/use-accordion';
import { useEnableAccordion } from '@/modules/perseo-billing/components/accordion/use-enable-accordion';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { setActiveAccordion } from '@/modules/perseo-billing/store/slices/billing';

import { CencoPay } from '../cencopay/cencopay.component';
import { GiftCardSection } from '../gift-card';
import { CustomSpreedlyForm } from '../spreedly-cards/spreedly-cards.component';
import { Webpay } from '../webpay/webpay.component';

import { PaymentMethodHeader } from './header/payment-method-header.component';
import { AccordionTemplate } from './accordion-template.component';
import type { TPaymentMethodProps } from './interfaces';

export const PaymentMethod = ({
  paymentMethod,
  index,
  accordionIndex,
  defaultExpanded = false,
  alwaysExpanded = false
}: TPaymentMethodProps) => {
  const { getActiveItem, onHeaderClick } = useAccordion();
  const { getPaymentMethodEnabled } = useEnableAccordion();
  const dispatch = useAppDispatch();

  const accordionindex: AccordionIndex = {
    accordionIndex: accordionIndex ?? null,
    itemIndex: index ?? null
  };

  const isActive = getActiveItem(accordionindex);

  const methodId = paymentMethod.methodId;
  const isEnabled = getPaymentMethodEnabled(methodId as TPaymentMethodId);

  const handleClick = () => {
    if (alwaysExpanded) return;
    onHeaderClick(accordionindex, methodId as TPaymentMethodId);
  };

  useEffect(() => {
    if (defaultExpanded && !isActive) {
      dispatch(
        setActiveAccordion({
          activeAccordionIndex: accordionIndex ?? undefined,
          activeItemIndex: index ?? undefined,
          accordionPaymentMethodId: methodId as TPaymentMethodId
        })
      );
    }
  }, [defaultExpanded, isActive, accordionIndex, index, methodId, dispatch]);

  return (
    <AccordionTemplate
      isActive={isActive}
      isEnabled={isEnabled}
      header={
        <PaymentMethodHeader
          paymentMethod={paymentMethod}
          isActive={isActive}
          handleClick={handleClick}
          alwaysExpanded={alwaysExpanded}
        />
      }
      body={
        <>
          {methodId === PaymentProcessor.CENCOPAY && <CencoPay />}
          {methodId === PaymentProcessor.WEBPAY && <Webpay method={methodId} />}
          {methodId === PaymentProcessor.TRANSBANK_CENCOSUD_CARD && (
            <CustomSpreedlyForm methodId={methodId} />
          )}
          {methodId === PaymentProcessor.TRANSBANK && (
            <CustomSpreedlyForm methodId={methodId} />
          )}
          {methodId === PaymentProcessor.GIFT_CARD && <GiftCardSection />}
        </>
      }
    />
  );
};
